import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  billingCurrencies,
  type BillingCurrency,
  type FeePlanVersionView,
  type FeePlansView,
  type FeeRunCommittedView,
  type FeeRunPreviewView,
  type FeeRunRowView,
  type SupportedLocale,
} from '@stm/contracts';
import { translate } from '@stm/i18n';
import { apiRequest } from './api';
import { formatCalendarDate } from './athletes';
import {
  amountInputText,
  feeRunOutcomeKeys,
  feeRunSkipReasonKeys,
  formatMoney,
  parseAmountInput,
  parseMonthInput,
} from './billing';
import { errorKeyOf, type ErrorKey } from './BillingForms';
import { FeeRunAutomation } from './FeeRunAutomation';

export interface BillingFeesPanelProps {
  locale: SupportedLocale;
  organizationId: string;
  csrfToken: string;
}

const currentMonthInput = (): string => new Date().toISOString().slice(0, 7);
const todayInUtc = (): string => new Date().toISOString().slice(0, 10);

/** A plan's newest version, which is the one a new version is based on. */
function newestVersions(versions: readonly FeePlanVersionView[]): FeePlanVersionView[] {
  const newest = new Map<string, FeePlanVersionView>();
  for (const version of versions) {
    const current = newest.get(version.planId);
    if (current === undefined || version.versionNumber > current.versionNumber) {
      newest.set(version.planId, version);
    }
  }
  return [...newest.values()];
}

function planName(plan: FeePlanVersionView, locale: SupportedLocale): string {
  if (locale === 'hu') return plan.nameHu ?? plan.nameEn;
  if (locale === 'ro') return plan.nameRo ?? plan.nameEn;
  return plan.nameEn;
}

/**
 * Finance's fee workspace: the fee plans (a plan version is never edited; a change is a new
 * version) and the monthly fee run, previewed first and then posted with one idempotency key.
 */
export function BillingFeesPanel({
  locale,
  organizationId,
  csrfToken,
}: BillingFeesPanelProps): React.JSX.Element {
  const base = `/api/v1/organizations/${organizationId}/billing`;
  const [plans, setPlans] = useState<FeePlanVersionView[] | null>(null);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [planSaved, setPlanSaved] = useState(false);
  // The plan a new version is based on; null starts a new plan.
  const [versionOf, setVersionOf] = useState<FeePlanVersionView | null>(null);
  const [nameEn, setNameEn] = useState('');
  const [nameHu, setNameHu] = useState('');
  const [nameRo, setNameRo] = useState('');
  const [amountText, setAmountText] = useState('');
  const [currency, setCurrency] = useState<BillingCurrency>('RON');
  const [dueDay, setDueDay] = useState('5');
  const [effectiveFrom, setEffectiveFrom] = useState(todayInUtc);
  const [effectiveTo, setEffectiveTo] = useState('');

  const [monthText, setMonthText] = useState(currentMonthInput);
  const [preview, setPreview] = useState<FeeRunPreviewView | null>(null);
  const [reviewTexts, setReviewTexts] = useState<Record<string, string>>({});
  const [committed, setCommitted] = useState<FeeRunCommittedView | null>(null);
  const [showMonthProblem, setShowMonthProblem] = useState(false);
  const commitKey = useRef<{ signature: string; key: string } | null>(null);

  const loadPlans = useCallback(async () => {
    try {
      const planList = await apiRequest<FeePlansView>(`${base}/fee-plans`);
      setPlans(planList.items);
      // A new plan starts in the club's currency (set under Club data on the Settings page).
      const clubCurrency = billingCurrencies.find((option) => option === planList.clubCurrency);
      if (clubCurrency !== undefined) setCurrency(clubCurrency);
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }, [base]);

  useEffect(() => {
    void loadPlans();
  }, [loadPlans]);

  const amountMinor = parseAmountInput(amountText);
  const dueDayNumber = Number(dueDay);
  const planFormValid =
    nameEn.trim() !== '' &&
    amountMinor !== null &&
    Number.isInteger(dueDayNumber) &&
    dueDayNumber >= 1 &&
    dueDayNumber <= 31 &&
    effectiveFrom !== '' &&
    (effectiveTo === '' || effectiveTo >= effectiveFrom);

  function startVersionOf(version: FeePlanVersionView | null): void {
    setVersionOf(version);
    setPlanSaved(false);
    if (version === null) return;
    setNameEn(version.nameEn);
    setNameHu(version.nameHu ?? '');
    setNameRo(version.nameRo ?? '');
    setAmountText(amountInputText(version.amountMinor));
    setCurrency(version.currency as BillingCurrency);
    setDueDay(String(version.dueDay));
    setEffectiveFrom(todayInUtc());
    setEffectiveTo('');
  }

  async function savePlan(): Promise<void> {
    if (amountMinor === null) return;
    setErrorKey(null);
    try {
      const path =
        versionOf === null ? `${base}/fee-plans` : `${base}/fee-plans/${versionOf.planId}/versions`;
      await apiRequest(path, {
        method: 'POST',
        csrfToken,
        body: {
          nameEn: nameEn.trim(),
          nameHu: nameHu.trim() === '' ? null : nameHu.trim(),
          nameRo: nameRo.trim() === '' ? null : nameRo.trim(),
          amountMinor,
          currency,
          dueDay: dueDayNumber,
          effectiveFrom,
          effectiveTo: effectiveTo === '' ? null : effectiveTo,
        },
      });
      setPlanSaved(true);
      setVersionOf(null);
      setNameEn('');
      setNameHu('');
      setNameRo('');
      setAmountText('');
      await loadPlans();
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }

  const month = parseMonthInput(monthText);

  async function loadPreview(period: { year: number; month: number }): Promise<void> {
    try {
      setPreview(
        await apiRequest<FeeRunPreviewView>(`${base}/fee-runs/preview`, {
          method: 'POST',
          csrfToken,
          body: period,
        }),
      );
      setReviewTexts({});
    } catch (error) {
      setPreview(null);
      setErrorKey(errorKeyOf(error));
    }
  }

  async function previewRun(): Promise<void> {
    if (month === null) {
      setShowMonthProblem(true);
      return;
    }
    setShowMonthProblem(false);
    setErrorKey(null);
    setCommitted(null);
    await loadPreview(month);
  }

  const reviewRows = preview?.rows.filter((row) => row.outcome === 'review_first_charge') ?? [];
  const reviewedLines = reviewRows.flatMap((row) => {
    const text = reviewTexts[row.feeAssignmentId] ?? '';
    const amount = text.trim() === '' ? null : parseAmountInput(text);
    return amount === null ? [] : [{ feeAssignmentId: row.feeAssignmentId, amountMinor: amount }];
  });
  const hasInvalidReview = reviewRows.some((row) => {
    const text = reviewTexts[row.feeAssignmentId] ?? '';
    return text.trim() !== '' && parseAmountInput(text) === null;
  });
  const canCommit =
    preview !== null &&
    month !== null &&
    !hasInvalidReview &&
    (preview.generateCount > 0 || reviewedLines.length > 0);

  async function commitRun(): Promise<void> {
    if (month === null || !canCommit) return;
    setErrorKey(null);
    // One key per content: a retry after a dropped connection repeats it; changed content gets a new one.
    const signature = JSON.stringify({ month, reviewedLines });
    if (commitKey.current?.signature !== signature) {
      commitKey.current = { signature, key: crypto.randomUUID() };
    }
    try {
      setCommitted(
        await apiRequest<FeeRunCommittedView>(`${base}/fee-runs/commit`, {
          method: 'POST',
          csrfToken,
          idempotencyKey: commitKey.current.key,
          body: { ...month, reviewed: reviewedLines },
        }),
      );
      commitKey.current = null;
      await loadPreview(month);
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }

  function statusLabel(row: FeeRunRowView): string {
    const outcome = translate(locale, feeRunOutcomeKeys[row.outcome]);
    return row.skipReason === null
      ? outcome
      : `${outcome}: ${translate(locale, feeRunSkipReasonKeys[row.skipReason])}`;
  }

  return (
    <Box component="section" aria-labelledby="fees-title" sx={{ mt: 4 }}>
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}

      <Typography variant="h6" component="h2" id="fees-title" gutterBottom>
        {translate(locale, 'billing.fees.plans.title')}
      </Typography>
      {plans?.length === 0 && (
        <Typography>{translate(locale, 'billing.fees.plans.empty')}</Typography>
      )}
      <Stack component="ul" spacing={1} sx={{ listStyle: 'none', padding: 0, margin: 0, mb: 2 }}>
        {newestVersions(plans ?? []).map((plan) => (
          <Box
            component="li"
            key={plan.planId}
            sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}
          >
            <Typography component="span">
              {planName(plan, locale)} · {formatMoney(plan.amountMinor, plan.currency, locale)} ·{' '}
              {translate(locale, 'billing.fees.plan.dueDay')}: {String(plan.dueDay)} ·{' '}
              {translate(locale, 'billing.fees.plan.version')} {String(plan.versionNumber)} ·{' '}
              {formatCalendarDate(plan.effectiveFrom, locale)}
              {plan.effectiveTo !== null && ` – ${formatCalendarDate(plan.effectiveTo, locale)}`}
            </Typography>
            <Button
              size="small"
              onClick={() => {
                startVersionOf(plan);
              }}
            >
              {translate(locale, 'billing.fees.plan.newVersion')}
            </Button>
          </Box>
        ))}
      </Stack>

      <Typography variant="subtitle1" component="h3" gutterBottom>
        {translate(
          locale,
          versionOf === null
            ? 'billing.fees.plans.create.title'
            : 'billing.fees.plans.create.titleVersion',
        )}
      </Typography>
      {planSaved && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {translate(locale, 'billing.fees.plan.saved')}
        </Alert>
      )}
      <Stack
        component="form"
        spacing={2}
        sx={{ mb: 3, maxWidth: 560 }}
        onSubmit={(event) => {
          event.preventDefault();
          void savePlan();
        }}
      >
        <TextField
          required
          size="small"
          label={translate(locale, 'billing.fees.plan.name.en')}
          value={nameEn}
          onChange={(event) => {
            setNameEn(event.target.value);
          }}
          slotProps={{ htmlInput: { maxLength: 120 } }}
        />
        <TextField
          size="small"
          label={translate(locale, 'billing.fees.plan.name.hu')}
          value={nameHu}
          onChange={(event) => {
            setNameHu(event.target.value);
          }}
          slotProps={{ htmlInput: { maxLength: 120 } }}
        />
        <TextField
          size="small"
          label={translate(locale, 'billing.fees.plan.name.ro')}
          value={nameRo}
          onChange={(event) => {
            setNameRo(event.target.value);
          }}
          slotProps={{ htmlInput: { maxLength: 120 } }}
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            required
            size="small"
            label={translate(locale, 'billing.fees.plan.amount')}
            value={amountText}
            error={amountText.trim() !== '' && amountMinor === null}
            helperText={
              amountText.trim() !== '' && amountMinor === null
                ? translate(locale, 'billing.invalidAmount')
                : undefined
            }
            onChange={(event) => {
              setAmountText(event.target.value);
            }}
            slotProps={{ htmlInput: { inputMode: 'decimal' } }}
            sx={{ flexGrow: 1 }}
          />
          <TextField
            select
            size="small"
            label={translate(locale, 'billing.fees.plan.currency')}
            value={currency}
            disabled={versionOf !== null}
            onChange={(event) => {
              setCurrency(event.target.value as BillingCurrency);
            }}
            sx={{ minWidth: 120 }}
          >
            {billingCurrencies.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <TextField
          required
          size="small"
          type="number"
          label={translate(locale, 'billing.fees.plan.dueDay')}
          helperText={translate(locale, 'billing.fees.plan.dueDayHelp')}
          value={dueDay}
          onChange={(event) => {
            setDueDay(event.target.value);
          }}
          slotProps={{ htmlInput: { min: 1, max: 31 } }}
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            required
            size="small"
            type="date"
            label={translate(locale, 'billing.fees.plan.effectiveFrom')}
            value={effectiveFrom}
            onChange={(event) => {
              setEffectiveFrom(event.target.value);
            }}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ flexGrow: 1 }}
          />
          <TextField
            size="small"
            type="date"
            label={translate(locale, 'billing.fees.plan.effectiveTo')}
            value={effectiveTo}
            onChange={(event) => {
              setEffectiveTo(event.target.value);
            }}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ flexGrow: 1 }}
          />
        </Stack>
        {!planFormValid && (nameEn !== '' || amountText !== '') && (
          <Typography variant="body2" color="text.secondary">
            {translate(locale, 'billing.fees.plan.invalid')}
          </Typography>
        )}
        <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
          <Button type="submit" variant="contained" disabled={!planFormValid}>
            {translate(locale, 'billing.fees.plan.submit')}
          </Button>
          {versionOf !== null && (
            <Button
              onClick={() => {
                setVersionOf(null);
              }}
            >
              {translate(locale, 'billing.fees.plan.startNew')}
            </Button>
          )}
        </Stack>
      </Stack>

      <Typography variant="h6" component="h2" id="fee-run-title" gutterBottom>
        {translate(locale, 'billing.fees.run.title')}
      </Typography>
      <FeeRunAutomation locale={locale} organizationId={organizationId} csrfToken={csrfToken} />
      <Stack
        component="form"
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ mb: 2 }}
        onSubmit={(event) => {
          event.preventDefault();
          void previewRun();
        }}
      >
        <TextField
          required
          size="small"
          type="month"
          label={translate(locale, 'billing.fees.run.month')}
          value={monthText}
          error={showMonthProblem}
          helperText={
            showMonthProblem ? translate(locale, 'billing.fees.run.invalidMonth') : undefined
          }
          onChange={(event) => {
            setMonthText(event.target.value);
            setPreview(null);
            setCommitted(null);
          }}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <Button type="submit" variant="outlined">
          {translate(locale, 'billing.fees.run.preview')}
        </Button>
      </Stack>

      {committed !== null && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {translate(locale, 'billing.fees.run.committed')}{' '}
          {translate(locale, 'billing.fees.run.result.created')}: {String(committed.createdCount)} ·{' '}
          {translate(locale, 'billing.fees.run.result.billed')}:{' '}
          {String(committed.alreadyBilledCount)} ·{' '}
          {translate(locale, 'billing.fees.run.result.review')}:{' '}
          {String(committed.awaitingReviewCount)} ·{' '}
          {translate(locale, 'billing.fees.run.result.skipped')}: {String(committed.skippedCount)}
        </Alert>
      )}

      {preview !== null && (
        <Box>
          {preview.rows.length === 0 && (
            <Typography>{translate(locale, 'billing.fees.run.empty')}</Typography>
          )}
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Chip
              label={`${translate(locale, 'billing.fees.run.count.generate')}: ${String(preview.generateCount)}`}
            />
            <Chip
              label={`${translate(locale, 'billing.fees.run.count.review')}: ${String(preview.reviewCount)}`}
            />
            <Chip
              label={`${translate(locale, 'billing.fees.run.count.billed')}: ${String(preview.alreadyBilledCount)}`}
            />
            <Chip
              label={`${translate(locale, 'billing.fees.run.count.skipped')}: ${String(preview.skippedCount)}`}
            />
            {preview.totals.map((total) => (
              <Chip
                key={total.currency}
                color="primary"
                label={`${translate(locale, 'billing.fees.run.total')}: ${formatMoney(total.amountMinor, total.currency, locale)}`}
              />
            ))}
          </Stack>
          {preview.rows.length > 0 && (
            <TableContainer sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{translate(locale, 'billing.fees.run.col.athlete')}</TableCell>
                    <TableCell>{translate(locale, 'billing.fees.run.col.account')}</TableCell>
                    <TableCell>{translate(locale, 'billing.fees.run.col.plan')}</TableCell>
                    <TableCell align="right">
                      {translate(locale, 'billing.fees.run.col.amount')}
                    </TableCell>
                    <TableCell>{translate(locale, 'billing.fees.run.col.due')}</TableCell>
                    <TableCell>{translate(locale, 'billing.fees.run.col.status')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.rows.map((row) => (
                    <TableRow key={row.feeAssignmentId}>
                      <TableCell>{row.athleteName}</TableCell>
                      <TableCell>{row.accountLabel}</TableCell>
                      <TableCell>{row.planName}</TableCell>
                      <TableCell align="right">
                        {formatMoney(row.amountMinor, row.currency, locale)}
                        {row.amountMinor !== row.baseAmountMinor && row.outcome !== 'skip' && (
                          <Typography variant="caption" component="div" color="text.secondary">
                            {formatMoney(row.baseAmountMinor, row.currency, locale)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{formatCalendarDate(row.dueOn, locale)}</TableCell>
                      <TableCell>{statusLabel(row)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {reviewRows.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {translate(locale, 'billing.fees.run.review.help')}
              </Typography>
              <Stack spacing={1}>
                {reviewRows.map((row) => {
                  const text = reviewTexts[row.feeAssignmentId] ?? '';
                  const invalid = text.trim() !== '' && parseAmountInput(text) === null;
                  return (
                    <TextField
                      key={row.feeAssignmentId}
                      size="small"
                      label={`${row.athleteName} – ${translate(locale, 'billing.fees.run.review.amount')} (${row.currency})`}
                      value={text}
                      error={invalid}
                      helperText={
                        invalid ? translate(locale, 'billing.fees.run.review.invalid') : undefined
                      }
                      onChange={(event) => {
                        setReviewTexts((current) => ({
                          ...current,
                          [row.feeAssignmentId]: event.target.value,
                        }));
                      }}
                      slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                      sx={{ maxWidth: 420 }}
                    />
                  );
                })}
              </Stack>
            </Box>
          )}
          <Button
            variant="contained"
            disabled={!canCommit}
            onClick={() => {
              void commitRun();
            }}
          >
            {translate(locale, 'billing.fees.run.commit')}
          </Button>
        </Box>
      )}
    </Box>
  );
}
